import { useState, useEffect, useCallback } from 'react';
import {
  viewContactRequest,
  ViewContactRequestData,
  type ViewContactRequestSource,
} from '../api/viewContactRequestApiService';
import type { ContactRequestStatus } from '../types/contactRequestTypes';
import { CONTACT_REQUEST_LABELS as L } from '../constants/labels';

/**
 * Visual config for every possible contact request status.
 * Kept here (not in types/) because it owns presentational values (colors)
 * shared between the list badge and the detail page header badge.
 */
const STATUS_CONFIG: Record<
  ContactRequestStatus,
  { label: string; bg: string; text: string }
> = {
  pending: { label: 'Otwarte', bg: 'rgba(230,184,0,0.16)', text: '#E6B800' },
  in_review: {
    label: 'W trakcie weryfikacji',
    bg: '#eaf3ff',
    text: '#1e7ce8',
  },
  assigned: { label: 'Przydzielone', bg: '#F1EAFB', text: '#7C3AED' },
  in_progress: {
    label: 'W realizacji',
    bg: 'rgba(255,152,0,0.16)',
    text: '#FF9800',
  },
  completed: { label: 'Zakończone', bg: '#E6F6F0', text: '#00AB76' },
  cancelled: { label: 'Anulowane', bg: '#FDE8E8', text: '#E11B1B' },
};

/**
 * Statuses offered in the plain "Zmiana statusu" dropdown per current status.
 *
 * This intentionally omits the transitions driven by other flows:
 * - `in_review → assigned` is performed via the Assign button, not here.
 * - `in_progress → completed` is performed via the dedicated "Complete & send
 *   response" flow (RR-1097), which requires a note or attachment, so
 *   `completed` is never offered as a plain dropdown option.
 */
const ALLOWED_NEXT_STATUSES: Record<
  ContactRequestStatus,
  ContactRequestStatus[]
> = {
  pending: ['in_review', 'cancelled'],
  in_review: ['cancelled'],
  assigned: ['in_progress', 'cancelled'],
  in_progress: ['cancelled'],
  completed: [],
  cancelled: [],
};

/** Returns the statuses that the admin can transition to from the given status. */
export function getAllowedContactRequestNextStatuses(
  current: ContactRequestStatus
): ContactRequestStatus[] {
  return ALLOWED_NEXT_STATUSES[current] ?? [];
}

/**
 * Returns the display label and badge colors for a contact request status.
 * Falls back to a neutral grey config for unknown/future statuses.
 *
 * @param status - Raw status string from the API
 */
export function getContactRequestStatusConfig(status: string): {
  label: string;
  bg: string;
  text: string;
} {
  return (
    STATUS_CONFIG[status as ContactRequestStatus] ?? {
      label: status,
      bg: '#E8EEF0',
      text: '#121F28',
    }
  );
}

/**
 * Formats an ISO-8601 date string into the admin panel display format:
 * `YYYY/MM/DD, HH:MM:SS` (local time, zero-padded).
 *
 * Returns `'—'` for missing or unparseable values so callers never
 * need to guard against `undefined` in JSX.
 *
 * @param iso - ISO-8601 date string from the API
 */
export function formatContactRequestDate(
  iso: string | null | undefined
): string {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return '—';
  }
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const seconds = String(d.getUTCSeconds()).padStart(2, '0');
  return `${year}/${month}/${day}, ${hours}:${minutes}:${seconds}`;
}

/**
 * Maps a raw role string from the API to its Polish display label.
 * Returns the raw value unchanged for unknown/future roles, and `'—'` when
 * absent, so callers never render an empty role.
 *
 * @param role - raw role string (e.g. `ADMIN`, `CLIENT`)
 */
export function formatContactRequestRole(
  role: string | null | undefined
): string {
  switch (role) {
    case 'CLIENT':
      return L.VIEW_CR_ROLE_CLIENT;
    case 'ADMIN':
      return L.VIEW_CR_ROLE_ADMIN;
    case 'SUPER_ADMIN':
      return L.VIEW_CR_ROLE_SUPER_ADMIN;
    case 'TECHNICIAN':
      return L.VIEW_CR_ROLE_TECHNICIAN;
    default:
      return role || '—';
  }
}

export interface UseViewContactRequestReturn {
  data: ViewContactRequestData | undefined;
  isLoading: boolean;
  showErrorScreen: boolean;
  accessError: boolean;
  /** Re-fetches from the API — used by error screens to offer a retry action. */
  refetch: () => Promise<void>;
}

/**
 * Fetches and holds the full detail of a single contact request.
 *
 * Fetch is triggered on mount and whenever `id` or `source` changes.
 * `isLoading` is always reset in the `finally` block so the spinner
 * never gets stuck even if the API service throws unexpectedly.
 *
 * @param id - UUID of the contact request (from the route param)
 * @param source - which detail endpoint to read (defaults to `'default'`;
 *   `'my-tasks'` reads the self-scoped assigned-task endpoint, RR-1096)
 */
export const useViewContactRequest = (
  id: string,
  source: ViewContactRequestSource = 'default'
): UseViewContactRequestReturn => {
  const [data, setData] = useState<ViewContactRequestData | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const [accessError, setAccessError] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await viewContactRequest(id, source);
      if (result.success && result.data) {
        setData(result.data);
        setShowErrorScreen(false);
      } else if (result.forbidden) {
        setAccessError(true);
        setShowErrorScreen(false);
      } else {
        setShowErrorScreen(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, source]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, showErrorScreen, accessError, refetch: fetchData };
};
